import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Shield, Building, Briefcase, FolderTree, Edit3, Eye, Lock, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';

const DepartmentTreeItem = ({ dept, level = 0, onEdit, onDelete, onView, t, canEdit, canDelete, canView }) => {
  const [showSub, setShowSub] = useState(true);
  const hasChildren = dept._children && dept._children.length > 0;

  return (
    <div>
      <div
        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer group border-b border-slate-100 dark:border-slate-800"
        style={{ paddingLeft: `${16 + level * 24}px` }}
        onClick={() => { if (hasChildren) setShowSub(!showSub); }}
      >
        <div className="shrink-0 text-slate-400">
          {hasChildren ? (
            showSub ? <FolderTree size={16} className="text-amber-500" /> : <FolderTree size={16} className="text-amber-500" />
          ) : (
            <Building size={16} className="text-slate-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-800 dark:text-slate-200 truncate">{dept.name}</div>
          {dept.description && (
            <div className="text-xs text-slate-400 dark:text-slate-500 truncate">{dept.description}</div>
          )}
        </div>
        <div className="text-xs text-slate-400 dark:text-slate-500 shrink-0">
          {t('common.sort_order', {order: dept.sort_order})}
        </div>
        {(canView || canEdit || canDelete) && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            {canView && (
              <button onClick={(e) => { e.stopPropagation(); onView(dept); }} className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
                <Eye size={15} />
              </button>
            )}
            {canEdit && (
              <button onClick={(e) => { e.stopPropagation(); onEdit(dept); }} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors">
                <Edit3 size={15} />
              </button>
            )}
            {canDelete && (
              <button onClick={(e) => { e.stopPropagation(); onDelete(dept); }} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                <Trash2 size={15} />
              </button>
            )}
          </div>
        )}
      </div>
      {hasChildren && showSub && (
        <div>
          {dept._children.map(child => (
            <DepartmentTreeItem key={child.id} dept={child} level={level + 1} onEdit={onEdit} onDelete={onDelete} onView={onView} t={t} canEdit={canEdit} canDelete={canDelete} canView={canView} />
          ))}
        </div>
      )}
    </div>
  );
};

const OrganizationManagement = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { hasPermission } = usePermission();

  // Permission checks
  const canViewDept = hasPermission('org_department', 'view');
  const canEditDept = hasPermission('org_department', 'edit');
  const canDeleteDept = hasPermission('org_department', 'delete');
  const canAddDept = hasPermission('org_department', 'add');
  const canViewPosition = hasPermission('org_position', 'view');
  const canEditPosition = hasPermission('org_position', 'edit');
  const canDeletePosition = hasPermission('org_position', 'delete');
  const canAddPosition = hasPermission('org_position', 'add');
  const canManagePermissions = hasPermission('organization', 'view') || hasPermission('organization', 'edit');

  const [activeTab, setActiveTab] = useState('departments');
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDepartments = async () => {
    try {
      const res = await fetch('/api/organization/departments');
      const data = await res.json();
      if (data.status === 'success') {
        const flat = [...data.data];
        const map = {};
        flat.forEach(d => { map[d.id] = { ...d, _children: [] }; });
        const roots = [];
        flat.forEach(d => {
          if (d.parent_id && map[d.parent_id]) {
            map[d.parent_id]._children.push(map[d.id]);
          } else {
            roots.push(map[d.id]);
          }
        });
        setDepartments(roots);
      }
    } catch (err) {
      console.error("Failed to load departments:", err);
    }
  };

  const loadPositions = async () => {
    try {
      const res = await fetch('/api/organization/positions');
      const data = await res.json();
      if (data.status === 'success') {
        setPositions(data.data);
      }
    } catch (err) {
      console.error("Failed to load positions:", err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadDepartments(), loadPositions()]);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleDeleteDept = async (dept) => {
    if (!window.confirm(t('common.confirm_delete') + ` "${dept.name}"?`)) return;
    try {
      const res = await fetch(`/api/organization/departments/${dept.id}/delete`, { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') {
        notify(t('common.success'), 'success');
        loadDepartments();
      } else {
        notify(data.message || t('common.error'), 'error');
      }
    } catch (err) {
      notify(t('common.error'), 'error');
    }
  };

  const handleDeletePosition = async (pos) => {
    if (!window.confirm(t('common.confirm_delete') + ` "${pos.name}"?`)) return;
    try {
      const res = await fetch(`/api/organization/positions/${pos.id}/delete`, { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') {
        notify(t('common.success'), 'success');
        loadPositions();
      } else {
        notify(data.message || t('common.error'), 'error');
      }
    } catch (err) {
      notify(t('common.error'), 'error');
    }
  };

  const handleViewDept = (dept) => {
    navigate(`/departments/view/${dept.id}`);
  };

  const handleEditDept = (dept) => {
    navigate(`/departments/edit/${dept.id}`);
  };

  const handleViewPosition = (pos) => {
    navigate(`/positions/view/${pos.id}`);
  };

  const handleEditPosition = (pos) => {
    navigate(`/positions/edit/${pos.id}`);
  };

  return (
    <div className="space-y-6 p-4">
      {/* Tab header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Building className="text-blue-600" />
          {t('sidebar.organization_management')}
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1.5 w-fit">
        <button
          onClick={() => setActiveTab('departments')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'departments'
              ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
          }`}
        >
          <Building size={16} />{t("common.departments")}
        </button>
        <button
          onClick={() => setActiveTab('positions')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'positions'
              ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
          }`}
        >
          <Briefcase size={16} />{t("common.positions")}
        </button>
      </div>

      {/* Department tab */}
      {activeTab === 'departments' && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
            <h2 className="font-medium text-slate-700 dark:text-slate-300">{t("common.department_list")}</h2>
            {canAddDept && (
              <button
                onClick={() => navigate('/departments/new')}
                className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm flex items-center gap-1.5 hover:bg-blue-700 transition-colors"
              >
                <Plus size={15} />{t("common.add_department")}
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-400">{t("common.loading")}</div>
          ) : departments.length === 0 ? (
            <div className="p-12 text-center text-slate-400">{t("common.no_departments")}</div>
          ) : (
            <div>
              {departments.map(dept => (
                <DepartmentTreeItem key={dept.id} dept={dept} onEdit={handleEditDept} onDelete={handleDeleteDept} onView={handleViewDept} t={t}
                  canEdit={canEditDept} canDelete={canDeleteDept} canView={canViewDept} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Position tab */}
      {activeTab === 'positions' && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
            <h2 className="font-medium text-slate-700 dark:text-slate-300">{t("common.position_list")}</h2>
            <div className="flex items-center gap-2">
              {canAddPosition && (
                <button
                  onClick={() => navigate('/positions/new')}
                  className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm flex items-center gap-1.5 hover:bg-blue-700 transition-colors"
                >
                  <Plus size={15} />{t("common.add_position")}
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-400">{t("common.loading")}</div>
          ) : positions.length === 0 ? (
            <div className="p-12 text-center text-slate-400">{t("common.no_positions")}</div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {positions.map(pos => (
                <div key={pos.id} className="px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 group">
                  <div className="flex flex-col md:flex-row md:items-center md:gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                        <Briefcase size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800 dark:text-slate-200">{pos.name}</span>
                          <span className="text-xs font-mono text-slate-400">{pos.code}</span>
                        </div>
                        <div className="text-xs text-slate-400 dark:text-slate-500 truncate">
                          {pos.department_name} · {pos.description || t('common.no_description')}
                        </div>
                      </div>
                      <div className="text-xs text-slate-400 shrink-0">{t('common.sort_order', {order: pos.sort_order})}</div>
                    </div>
                    {(canViewPosition || canEditPosition || canDeletePosition || canManagePermissions) && (
                      <div className="flex items-center gap-0.5 mt-1.5 md:mt-0 md:opacity-0 md:group-hover:opacity-100 md:transition-opacity shrink-0">
                        {canManagePermissions && (
                          <button
                            onClick={() => navigate(`/permissions?position_id=${pos.id}`)}
                            className="p-1.5 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title={t('org.permission_management')}
                          >
                            <Shield size={15} />
                          </button>
                        )}
                        {canViewPosition && (
                          <button onClick={() => handleViewPosition(pos)} className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
                            <Eye size={15} />
                          </button>
                        )}
                        {canEditPosition && (
                          <button onClick={() => handleEditPosition(pos)} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors">
                            <Edit3 size={15} />
                          </button>
                        )}
                        {canDeletePosition && (
                          <button onClick={() => handleDeletePosition(pos)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrganizationManagement;
